import { clamp, mulberry32 } from '../utils/math.js';

export const ERAS = ['VOID', 'DUST', 'STARFIELD', 'CONSTELLATION', 'GALAXY'];

const NAME_A = ['KEPLER', 'VEGA', 'ORION', 'LYRA', 'NOVA', 'AURIGA', 'CYGNUS', 'HELIX', 'ATLAS', 'MIRA'];
const NAME_B = ['VELVET', 'EMBER', 'DRIFT', 'BLOOM', 'ECHO', 'PRISM', 'HALO', 'SABLE', 'AZURE', 'OPAL'];

/** Playfield ring kept free of cosmos elements for gameplay readability. */
const RING_INNER = 0.16;
const RING_OUTER = 0.42;

function insidePlayfieldRing(x, y) {
  const d = Math.hypot(x - 0.5, y - 0.5);
  return d > RING_INNER && d < RING_OUTER;
}

function round3(value) {
  return Math.round(value * 1000) / 1000;
}

/**
 * Full-screen seeded galactic composition, dark at song start.
 * Every success ignites the next elements; the universe reveals itself
 * without ever following the orbit path.
 */
export default class CosmosSystem {
  constructor() {
    this.reset();
  }

  reset() {
    this.layout = [];
    this.dust = [];
    this.nebulae = [];
    this.links = [];
    this.flashes = [];
    this.blackHoles = [];
    this.shockwaves = [];
    this.comets = [];
    this.bursts = [];
    this.sparks = [];
    this.core = { x: 0.5, y: 0.3 };
    this.overdriveGlow = 0;
    this.rng = Math.random;
    this.palette = { hues: [210, 250, 30, 135], hueShift: 0 };
    this.name = 'VOID';
    this.eraIndex = 0;
    this.rotation = 0;
    this.supernovae = 0;
    this.version = 1;
    this._nextSlot = 0;
    this._nextNebula = 0;
    this._lastLitByArm = new Map();
    this._time = 0;
    this._nextShootingStar = 7;
  }

  begin(seed) {
    this.reset();
    this.rng = mulberry32(seed >>> 0);
    const base = Math.floor(this.rng() * 360);
    this.palette = {
      hues: [base, (base + 40) % 360, (base + 180) % 360, (base + 285) % 360],
      hueShift: 0
    };
    const a = NAME_A[Math.floor(this.rng() * NAME_A.length)];
    const b = NAME_B[Math.floor(this.rng() * NAME_B.length)];
    this.name = `${a}-${10 + Math.floor(this.rng() * 90)} ${b}`;
    this._buildLayout();
  }

  /** Random point outside the playfield ring (seeded). */
  _pickOutside(minDist) {
    for (let attempt = 0; attempt < 60; attempt++) {
      const x = this.rng();
      const y = this.rng();
      const d = Math.hypot(x - 0.5, y - 0.5);
      if (d >= minDist && !insidePlayfieldRing(x, y)) return { x, y };
    }
    return { x: 0.1, y: 0.1 };
  }

  _buildLayout() {
    const rng = this.rng;
    const groups = [];

    // Spiral arms from an off-center galactic core.
    const gx = 0.5 + (rng() - 0.5) * 0.44;
    const gy = 0.5 + (rng() - 0.5) * 0.44;
    this.core = { x: gx, y: gy };
    const ARMS = 4;
    for (let a = 0; a < ARMS; a++) {
      const group = [];
      const phase = (Math.PI * 2 * a) / ARMS + rng() * 0.8;
      const twist = 3.6 + rng() * 1.6;
      for (let i = 0; i < 120; i++) {
        const t = i / 120;
        const radius = 0.05 + t * 0.98;
        const angle = phase + t * twist;
        // Re-jitter instead of dropping: the arms stay dense around the ring.
        for (let attempt = 0; attempt < 4; attempt++) {
          const x = gx + Math.cos(angle) * radius + (rng() - 0.5) * 0.08;
          const y = gy + Math.sin(angle) * radius * 0.85 + (rng() - 0.5) * 0.08;
          if (x < -0.04 || x > 1.04 || y < -0.04 || y > 1.04) continue;
          if (insidePlayfieldRing(x, y)) continue;
          group.push(this._makeSlot(x, y, this.palette.hues[a % 4], a));
          break;
        }
      }
      groups.push(group);
    }

    // Dense circular clusters filling the remaining space.
    for (let c = 0; c < 8; c++) {
      const group = [];
      const center = this._pickOutside(0.44);
      for (let i = 0; i < 15; i++) {
        for (let attempt = 0; attempt < 4; attempt++) {
          const angle = rng() * Math.PI * 2;
          const radius = Math.abs(rng() + rng() - 1) * 0.075;
          const x = center.x + Math.cos(angle) * radius;
          const y = center.y + Math.sin(angle) * radius;
          if (insidePlayfieldRing(x, y)) continue;
          group.push(this._makeSlot(
            clamp(x, -0.03, 1.03),
            clamp(y, -0.03, 1.03),
            this.palette.hues[(c + 1) % 4],
            4 + c
          ));
          break;
        }
      }
      groups.push(group);
    }

    // Round-robin interleave: the galaxy grows everywhere at once.
    this.layout = [];
    for (let index = 0, added = true; added; index++) {
      added = false;
      for (const group of groups) {
        if (group[index]) {
          this.layout.push(group[index]);
          added = true;
        }
      }
    }

    // Giant nebulae, ignited by completed holds. Each breathes on its own phase.
    this.nebulae = [];
    for (let n = 0; n < 10; n++) {
      const pos = this._pickOutside(0.46);
      this.nebulae.push({
        x: pos.x,
        y: pos.y,
        r: 0.16 + rng() * 0.18,
        hue: (this.palette.hues[n % 4] + (rng() - 0.5) * 20 + 360) % 360,
        lit: false,
        bright: 1,
        phase: rng() * Math.PI * 2
      });
    }

    // Ambient dust: faint pre-lit micro-stars so the sky is never empty.
    // They warm up as the era rises (alpha handled by the painter).
    this.dust = [];
    for (let guard = 0; this.dust.length < 140 && guard < 600; guard++) {
      const x = rng();
      const y = rng();
      if (insidePlayfieldRing(x, y)) continue;
      this.dust.push({
        x, y,
        size: 0.4 + rng() * 0.7,
        hue: (this.palette.hues[this.dust.length % 4] + (rng() - 0.5) * 50 + 360) % 360,
        tw: rng() * Math.PI * 2
      });
    }
  }

  _makeSlot(x, y, baseHue, arm) {
    const rng = this.rng;
    return {
      x, y,
      size: rng() < 0.09 ? 2.4 + rng() * 1.7 : 0.7 + rng() * 1.6,
      hue: (baseHue + (rng() - 0.5) * 70 + 360) % 360,
      arm,
      lit: false,
      core: false,
      bright: 1,
      tw: rng() * Math.PI * 2
    };
  }

  /** Comet crossing the whole screen, seeded to avoid the central ring. */
  spawnComet(ambient = false) {
    const fromLeft = this.rng() < 0.5;
    const topLane = this.rng() < 0.5;
    const y0 = topLane ? 0.02 + this.rng() * 0.1 : 0.86 + this.rng() * 0.1;
    const y1 = clamp(y0 + (this.rng() - 0.5) * 0.5, 0.02, 0.98);
    this.comets.push({
      x0: fromLeft ? -0.05 : 1.05,
      y0,
      x1: fromLeft ? 1.05 : -0.05,
      y1,
      hue: ambient
        ? (this.palette.hues[Math.floor(this.rng() * 4)] + this.palette.hueShift) % 360
        : (this.palette.hues[3] + this.palette.hueShift) % 360,
      t: 0,
      speed: ambient ? 0.8 + this.rng() * 0.4 : 0.55 + this.rng() * 0.3,
      small: ambient
    });
  }

  /** Combo milestone: full-screen colored rays from the galactic core + bonus ignitions. */
  burst() {
    this.bursts.push({
      x: this.core.x,
      y: this.core.y,
      hue: (this.palette.hues[Math.floor(this.rng() * 4)] + this.palette.hueShift) % 360,
      life: 1,
      rays: 12 + Math.floor(this.rng() * 5),
      seed: this.rng() * Math.PI * 2
    });
    for (let i = 0; i < 4; i++) this._igniteNext(false);
    this.version++;
  }

  setOverdrive(active) {
    this._overdriveTarget = active ? 1 : 0;
  }

  /** Golden perfect: localized mini-supernova + 6 stars ignited at once. */
  goldNova(x, y) {
    this.shockwaves.push({
      r: 0.01,
      life: 0.8,
      x: clamp(x, 0.05, 0.95),
      y: clamp(y, 0.05, 0.95),
      hue: 46,
      mini: true
    });
    for (let i = 0; i < 6; i++) this._igniteNext(i === 0);
    this.version++;
  }

  /** Overdrive hit feedback: short ray burst at the galactic core, no ignition. */
  miniBurst() {
    this.bursts.push({
      x: this.core.x,
      y: this.core.y,
      hue: (this.palette.hues[Math.floor(this.rng() * 4)] + this.palette.hueShift) % 360,
      life: 0.55,
      rays: 7,
      seed: this.rng() * Math.PI * 2,
      mini: true
    });
  }

  _igniteNext(big) {
    while (this._nextSlot < this.layout.length) {
      const slot = this.layout[this._nextSlot++];
      if (slot.lit) continue;
      slot.lit = true;
      slot.hue = (slot.hue + this.palette.hueShift) % 360;
      const previous = this._lastLitByArm.get(slot.arm);
      if (previous?.lit) this.links.push({ a: previous, b: slot });
      this._lastLitByArm.set(slot.arm, slot);
      this.flashes.push({ x: slot.x, y: slot.y, hue: slot.hue, life: 1, big });
      this.version++;
      return slot;
    }
    // Composition fully ignited: extra hits intensify a random lit star.
    const lit = this.layout.filter((s) => s.lit);
    if (!lit.length) return null;
    const slot = lit[Math.floor(this.rng() * lit.length)];
    slot.bright = Math.min(3, slot.bright + 0.3);
    this.flashes.push({ x: slot.x, y: slot.y, hue: slot.hue, life: 1, big });
    this.version++;
    return slot;
  }

  /** Tap success: ignite the next 1 (good) or 2 (perfect) layout stars. */
  addStar(quality = 'good') {
    this._igniteNext(quality === 'perfect');
    if (quality === 'perfect') this._igniteNext(false);
  }

  /** Completed hold: embrase the next giant nebula. */
  igniteNebula(quality = 'good') {
    const nebula = this.nebulae[this._nextNebula];
    if (nebula) {
      this._nextNebula++;
      nebula.lit = true;
      nebula.bright = quality === 'perfect' ? 1.25 : 1;
      nebula.hue = (nebula.hue + this.palette.hueShift) % 360;
      this.flashes.push({ x: nebula.x, y: nebula.y, hue: nebula.hue, life: 1, big: true });
    } else {
      for (const litNebula of this.nebulae) litNebula.bright = Math.min(1.8, litNebula.bright + 0.15);
      this._igniteNext(true);
    }
    this.version++;
  }

  /** SYNC bonus: the most crowded lit star becomes a blazing cluster core. */
  igniteCore() {
    const lit = this.layout.filter((s) => s.lit && !s.core);
    if (!lit.length) return;
    let best = lit[0];
    let bestCount = -1;
    for (const candidate of lit) {
      let count = 0;
      for (const other of lit) {
        if (Math.hypot(candidate.x - other.x, candidate.y - other.y) < 0.07) count++;
      }
      if (count > bestCount) {
        bestCount = count;
        best = candidate;
      }
    }
    best.core = true;
    best.size = Math.max(best.size * 1.8, 3);
    best.bright = 2;
    this.flashes.push({ x: best.x, y: best.y, hue: best.hue, life: 1, big: true });
    this.version++;
  }

  /** Miss: a black hole opens on a lit region and devours its stars. */
  addBlackHole() {
    const lit = this.layout.filter((s) => s.lit);
    const target = lit.length
      ? lit[Math.floor(this.rng() * lit.length)]
      : this._pickOutside(0.44);
    this.blackHoles.push({
      x: clamp(target.x, 0.05, 0.95),
      y: clamp(target.y, 0.05, 0.95),
      life: 3,
      maxLife: 3,
      radius: 0.12
    });
    this.version++;
  }

  supernova() {
    this.shockwaves.push({ r: 0.02, life: 1 });
    // Radial star shards thrown from the center.
    for (let i = 0; i < 20; i++) {
      const angle = (Math.PI * 2 * i) / 20 + this.rng() * 0.3;
      this.sparks.push({
        x: 0.5,
        y: 0.5,
        vx: Math.cos(angle) * (0.5 + this.rng() * 0.4),
        vy: Math.sin(angle) * (0.5 + this.rng() * 0.4),
        hue: (this.palette.hues[i % 4] + this.palette.hueShift) % 360,
        life: 1
      });
    }
    this.palette.hueShift = (this.palette.hueShift + 34) % 360;
    for (const slot of this.layout) {
      if (slot.lit) slot.hue = (slot.hue + 34) % 360;
    }
    for (const nebula of this.nebulae) {
      if (nebula.lit) nebula.hue = (nebula.hue + 34) % 360;
    }
    this.supernovae++;
    this.version++;
  }

  update(dt, maxCombo, progress) {
    this._time += dt;
    const litCount = this._nextSlot;

    let target = 0;
    if (litCount > 0) target = 1;
    if (litCount >= 25 || maxCombo >= 10) target = 2;
    if (maxCombo >= 25 && progress > 0.2) target = 3;
    if (maxCombo >= 50 && progress > 0.4) target = 4;
    if (target > this.eraIndex) {
      this.eraIndex = target;
      this.version++;
    }

    // Slow field drift from STARFIELD on, five times faster once a GALAXY.
    if (this.eraIndex >= 2) this.rotation += dt * 0.006 * (this.eraIndex >= 4 ? 5 : 1);

    // Ambient shooting stars keep the sky alive, combo or not.
    if (this._time >= this._nextShootingStar) {
      this.spawnComet(true);
      this._nextShootingStar = this._time + 7 + this.rng() * 4;
    }

    for (const flash of this.flashes) flash.life -= dt * 2.2;
    this.flashes = this.flashes.filter((f) => f.life > 0);

    if (this.blackHoles.length) {
      for (const hole of this.blackHoles) {
        hole.life -= dt;
        for (const slot of this.layout) {
          if (!slot.lit) continue;
          const dx = hole.x - slot.x;
          const dy = hole.y - slot.y;
          const d = Math.hypot(dx, dy);
          if (d > hole.radius || d < 1e-4) continue;
          const pull = (1 - d / hole.radius) * dt * 0.3;
          slot.x += (dx / d) * pull - (dy / d) * pull * 0.8;
          slot.y += (dy / d) * pull + (dx / d) * pull * 0.8;
          slot.bright -= dt * 0.9;
          if (slot.bright <= 0 || Math.hypot(hole.x - slot.x, hole.y - slot.y) < 0.012) {
            slot.lit = false;
            slot.core = false;
          }
        }
      }
      this.links = this.links.filter((l) => l.a.lit && l.b.lit);
      this.blackHoles = this.blackHoles.filter((h) => h.life > 0);
      this.version++;
    }

    for (const wave of this.shockwaves) {
      wave.r += dt * 0.6;
      wave.life -= dt * 0.65;
    }
    this.shockwaves = this.shockwaves.filter((w) => w.life > 0);

    for (const comet of this.comets) comet.t += dt * comet.speed;
    this.comets = this.comets.filter((c) => c.t < 1.15);

    for (const burst of this.bursts) burst.life -= dt * 1.7;
    this.bursts = this.bursts.filter((b) => b.life > 0);

    for (const spark of this.sparks) {
      spark.x += spark.vx * dt;
      spark.y += spark.vy * dt;
      spark.vx *= 0.985;
      spark.vy *= 0.985;
      spark.life -= dt * 0.8;
    }
    this.sparks = this.sparks.filter((s) => s.life > 0);

    // Incandescent Overdrive glow eases in and out.
    const glowTarget = this._overdriveTarget || 0;
    this.overdriveGlow += (glowTarget - this.overdriveGlow) * Math.min(1, dt * 4);
    if (this.overdriveGlow < 0.005 && glowTarget === 0) this.overdriveGlow = 0;
  }

  get era() {
    return ERAS[this.eraIndex];
  }

  getState() {
    return {
      stars: this.layout.filter((s) => s.lit),
      dust: this.dust,
      nebulae: this.nebulae.filter((n) => n.lit),
      links: this.links,
      flashes: this.flashes,
      blackHoles: this.blackHoles,
      shockwaves: this.shockwaves,
      comets: this.comets,
      bursts: this.bursts,
      sparks: this.sparks,
      overdriveGlow: this.overdriveGlow,
      palette: this.palette,
      era: this.era,
      eraIndex: this.eraIndex,
      rotation: this.rotation,
      name: this.name,
      time: this._time,
      version: this.version
    };
  }

  /** Compact serializable snapshot (v2) for the universe gallery. */
  snapshot() {
    const stars = this.layout.filter((s) => s.lit);
    const index = new Map(stars.map((s, i) => [s, i]));
    return {
      v: 2,
      name: this.name,
      era: this.era,
      eraIndex: this.eraIndex,
      hues: this.palette.hues.map((h) => Math.round(h)),
      hueShift: Math.round(this.palette.hueShift),
      supernovae: this.supernovae,
      stars: stars.map((s) => [
        round3(s.x),
        round3(s.y),
        Math.round(s.size * Math.min(2, s.bright) * 100) / 100,
        Math.round(s.hue),
        s.core ? 1 : 0
      ]),
      nebulae: this.nebulae
        .filter((n) => n.lit)
        .map((n) => [round3(n.x), round3(n.y), round3(n.r * n.bright), Math.round(n.hue)]),
      links: this.links
        .filter((l) => index.has(l.a) && index.has(l.b))
        .map((l) => [index.get(l.a), index.get(l.b)]),
      createdAt: Date.now()
    };
  }
}

let snapshotStateCounter = 0;

/** Rebuild a render-ready cosmos state from a stored snapshot. */
export function stateFromSnapshot(snap) {
  if (!snap) return null;
  snapshotStateCounter++;
  const stars = (snap.stars || []).map(([x, y, size, hue, core], i) => ({
    x, y, size, hue,
    core: core === 1,
    bright: 1,
    lit: true,
    tw: (i * 2.399) % (Math.PI * 2)
  }));
  return {
    stars,
    dust: [],
    nebulae: (snap.nebulae || []).map(([x, y, r, hue], i) => ({
      x, y, r, hue,
      lit: true,
      bright: 1,
      phase: (i * 1.618) % (Math.PI * 2)
    })),
    links: (snap.links || [])
      .filter(([a, b]) => stars[a] && stars[b])
      .map(([a, b]) => ({ a: stars[a], b: stars[b] })),
    flashes: [],
    blackHoles: [],
    shockwaves: [],
    comets: [],
    bursts: [],
    sparks: [],
    overdriveGlow: 0,
    palette: { hues: snap.hues || [210, 250, 30, 135], hueShift: snap.hueShift || 0 },
    era: snap.era,
    eraIndex: snap.eraIndex ?? 0,
    rotation: 0,
    name: snap.name,
    time: 0,
    version: `snap-${snapshotStateCounter}`
  };
}
